/**
 * Consumo del periodo contra los límites del plan, para la superficie del CLIENTE.
 *
 * Regla de este archivo, sin excepciones: **lo ausente sale como `null`, jamás
 * como `0`**. `formatNumber` ya pinta un guion ante `null`, así que el hueco
 * honesto sale gratis; el cero inventado hay que teclearlo.
 *
 * ─── Por qué aquí ya no hay dinero ───────────────────────────────────────────
 *
 * `GET /usage/current` devuelve `cost.costUsd` —lo que le cuesta a la
 * PLATAFORMA servir a ese cliente en llamadas al modelo— y una métrica de cuota
 * `model_spend_usd`. El panel del cliente los pintaba, y esa cifra no es suya:
 * el plan «Inicial» son 49 US$/mes y el consumo de modelo de sus 2.000 mensajes
 * ronda los 0,54 US$. Enseñárselo no le informa de nada que pueda usar —no es lo
 * que paga ni lo que recibe— y sí le regala el argumento para renegociar el
 * precio. **El coste de modelo es información interna de plataforma**, y vive en
 * `/platform/usage`, que es la consola del dueño.
 *
 * Por eso el recorte se hace AQUÍ y no en las plantillas: este normalizador es
 * la única puerta por la que ese importe entraba en `/app` (lo usan
 * `metricsApi.currentUsage` y el resumen del inicio, las dos de cliente). Si el
 * dato no llega a la vista, no hay plantilla que pueda volver a pintarlo por
 * descuido.
 *
 * Lo que sí se le entrega es lo que su plan mide y él entiende: mensajes,
 * conversaciones, sesiones de WhatsApp, usuarios, documentos y almacenamiento,
 * cada uno con usado, tope, restante y proporción. Eso responde a la pregunta
 * que de verdad tiene —«¿me voy a quedar corto este mes?»— y un importe no.
 */

/**
 * Métrica de cuota que mide el gasto en modelos.
 *
 * Se exporta para poder AFIRMAR en las pruebas que no aparece en lo que se
 * entrega al cliente, no para pintarla.
 */
export const MODEL_SPEND_METRIC = 'model_spend_usd'

/**
 * Métricas de cuota que se le enseñan al cliente, en el orden en que se leen.
 *
 * Son las claves de `quota.service.js § METRICS` menos `model_spend_usd`, y
 * ninguna es dinero: `kind` sólo vale `number` o `megabytes`. Que no exista un
 * `kind: 'money'` en esta lista es deliberado — es lo que impide que una métrica
 * monetaria vuelva a colarse en la superficie del cliente sin que se note.
 */
export const PLAN_USAGE_METRICS = Object.freeze([
  { key: 'messages', labelKey: 'organization.usageMessages', kind: 'number' },
  { key: 'conversations', labelKey: 'organization.limitConversations', kind: 'number' },
  { key: 'whatsapp_sessions', labelKey: 'organization.limitSessions', kind: 'number' },
  { key: 'email_mailboxes', labelKey: 'organization.usageMailboxes', kind: 'number' },
  { key: 'users', labelKey: 'organization.limitUsers', kind: 'number' },
  { key: 'documents', labelKey: 'organization.usageDocuments', kind: 'number' },
  { key: 'storage_mb', labelKey: 'organization.limitStorage', kind: 'megabytes' },
])

/**
 * Consumo presentable para el cliente.
 *
 * @param {Record<string, unknown>|null|undefined} raw respuesta de `/usage/current`
 *   o el objeto `usage` incrustado en el resumen del inicio.
 */
export function normalizeCurrentUsage(raw) {
  const source = object(raw)

  return {
    period: text(source.period) || null,
    plan: text(source.plan) || null,
    keyMode: text(source.keyMode ?? source.key_mode) || null,
    overagePolicy: text(source.overagePolicy ?? source.overage_policy) || null,

    /**
     * Métricas del plan SIN la de gasto: `cost` entero y `model_spend_usd` se
     * quedan en el sobre crudo y no cruzan esta función.
     */
    metrics: withoutModelSpend(normalizeMetrics(source.metrics)),

    /**
     * Desglose por canal sin los importes. El reparto del gasto
     * (`channelShare`) tampoco pasa: es el mismo coste de modelo en tanto por
     * uno, y repartirlo no lo convierte en información del cliente.
     */
    byChannel: list(source.byChannel).map(withoutMoneyFields),
  }
}

/**
 * A partir de este porcentaje de un tope se avisa al cliente.
 *
 * Vive aquí y no en cada plantilla porque el aviso sale en tres sitios (inicio,
 * métricas y organización) y tres umbrales distintos serían tres respuestas
 * distintas a la misma pregunta.
 */
export const NEAR_LIMIT_PERCENT = 80

/**
 * El consumo del plan como lista de filas presentables, una por métrica.
 *
 * El porcentaje se redondea aquí y en un solo sitio, a partir del `ratio` que ya
 * viene redondeado del backend: si cada vista lo recalcula a su manera, dos
 * pantallas enseñan cifras distintas del mismo mes.
 *
 * @param {Record<string, {used: number|null, limit: number|null, ratio: number|null}>} metrics
 */
export function planUsageRows(metrics) {
  const source = object(metrics)

  return PLAN_USAGE_METRICS.map((entry) => {
    const metric = object(source[entry.key])
    const ratio = metric.ratio ?? null

    return {
      ...entry,
      used: metric.used ?? null,
      limit: metric.limit ?? null,
      remaining: metric.remaining ?? null,
      // `null` cuando el plan no fija tope: «sin tope» y «al 0 %» son cosas
      // opuestas y no pueden pintarse igual.
      ratio,
      percent: ratio === null ? null : Math.min(100, Math.round(ratio * 100)),
    }
  })
}

/**
 * La métrica del plan que va más apretada, que es la única que cabe en una
 * tarjeta del inicio.
 *
 * `null` cuando ninguna tiene tope declarado: entonces no hay «la que peor va»,
 * y decir que se está al 0 % de algo sin límite no significa nada.
 *
 * @param {Record<string, unknown>} metrics
 */
export function nearestLimitMetric(metrics) {
  const withLimit = planUsageRows(metrics).filter((row) => row.ratio !== null)
  if (withLimit.length === 0) return null
  return withLimit.reduce((worst, row) => (row.ratio > worst.ratio ? row : worst))
}

/**
 * Mapa de métricas de cuota, cada una con lo suyo.
 *
 * Se conserva indexado por nombre de métrica y no se aplana a una lista: las
 * claves (`messages`, `model_spend_usd`, `whatsapp_sessions`…) son el contrato
 * de `quota.service.js § METRICS` y cada vista consulta la que le importa.
 *
 * @param {unknown} raw
 */
export function normalizeMetrics(raw) {
  const source = object(raw)
  const result = {}
  for (const [name, value] of Object.entries(source)) {
    const entry = object(value)
    result[name] = {
      used: number(entry.used),
      // `limit`, `remaining` y `ratio` llegan como `null` cuando el plan no
      // pone tope. Se respeta el `null`: «sin límite» y «límite de cero» son
      // situaciones opuestas y no pueden pintarse igual.
      limit: number(entry.limit),
      remaining: number(entry.remaining),
      ratio: number(entry.ratio),
    }
  }
  return result
}

/**
 * Fila de `GET /platform/usage`: consumo agregado por cliente, modelo o tarea.
 *
 * Es la pantalla que responde «¿gano o pierdo dinero?» y contestaba que no hay
 * negocio. Los cuatro datos que le faltaban existen todos, con otro nombre:
 *
 *  - `tokens` no existe; llegan `input_tokens` y `output_tokens` por separado;
 *  - `costUsd` es `cost_usd`;
 *  - `revenueUsd` es `billed_usd` — lo facturado al cliente;
 *  - el margen ni siquiera había que calcularlo: viene hecho en `margin_usd`.
 *
 * El margen se PROPAGA en vez de recalcularse. El backend lo define como
 * `sum(billed_usd) - sum(cost_usd)` sobre las filas crudas; restar aquí los dos
 * valores ya redondeados a cuatro decimales daría una cifra distinta de la del
 * backend en el peor momento posible, que es cuando alguien está cuadrando
 * facturas.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizePlatformUsageRow(raw) {
  const source = object(raw)
  const inputTokens = number(source.inputTokens ?? source.input_tokens)
  const outputTokens = number(source.outputTokens ?? source.output_tokens)

  return {
    key: text(source.key) || null,
    // Sin etiqueta se cae al `key`, que para `task` y `channel` ES el nombre
    // legible. Lo que no se hace es caer a un UUID y llamarlo nombre.
    label: text(source.label) || text(source.key) || null,
    events: number(source.events),
    inputTokens,
    outputTokens,
    tokens: inputTokens === null && outputTokens === null ? null : (inputTokens ?? 0) + (outputTokens ?? 0),
    costUsd: number(source.costUsd ?? source.cost_usd),
    billedUsd: number(source.billedUsd ?? source.billed_usd),
    marginUsd: number(source.marginUsd ?? source.margin_usd),
  }
}

/**
 * Totales del periodo.
 *
 * `GET /platform/usage` no manda totales —sólo `groupBy`, `period` e `items`—
 * así que se suman aquí. Se suma sólo lo que hay: si NINGUNA fila trae coste, el
 * total es `null` y la tarjeta pinta un guion. Antes salía un `$0.00` que se lee
 * como «este mes no hemos gastado nada», que es una afirmación muy distinta de
 * «no tengo el dato».
 *
 * @param {Array<ReturnType<typeof normalizePlatformUsageRow>>} rows
 */
export function sumPlatformUsage(rows = []) {
  const fields = ['tokens', 'costUsd', 'billedUsd', 'marginUsd']
  const totals = {}

  for (const field of fields) {
    const values = rows.map((row) => row?.[field]).filter((value) => value !== null && value !== undefined)
    totals[field] = values.length === 0 ? null : values.reduce((acc, value) => acc + value, 0)
  }

  return totals
}

/**
 * Margen en tanto por uno sobre lo facturado.
 *
 * `null` cuando no se ha facturado nada: dividir por cero no informa de nada, y
 * un «0 %» ahí se lee como «trabajamos a coste», que no es lo que pasa.
 *
 * @param {{ marginUsd?: number|null, billedUsd?: number|null }} row
 */
export function marginRatio(row = {}) {
  const billed = row.billedUsd
  const margin = row.marginUsd
  if (billed === null || billed === undefined || billed === 0) return null
  if (margin === null || margin === undefined) return null
  return margin / billed
}

/**
 * Periodo de facturación `YYYY-MM` en UTC.
 *
 * Ni `/platform/usage` ni `/usage/current` aceptan `from`/`to`: su único filtro
 * es `period`. Y como la validación usa `stripUnknown`, mandar un rango NO daba
 * error — se descartaba en silencio y se devolvía el mes en curso. La vista
 * creía estar enseñando 90 días y enseñaba el mes actual, sin un solo aviso.
 *
 * @param {Date} [date]
 */
export function toPeriod(date = new Date()) {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `${date.getUTCFullYear()}-${month}`
}

/**
 * Los últimos `count` periodos, del más reciente al más antiguo.
 * @param {number} count
 * @param {Date} [from]
 */
export function recentPeriods(count = 6, from = new Date()) {
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() - index, 1))
    return toPeriod(date)
  })
}

/* ─── Utilidades internas ─────────────────────────────────────────────────── */

/** Campos de dinero del contrato, en las dos grafías con que puede llegar. */
const MONEY_FIELDS = ['costUsd', 'cost_usd', 'billedUsd', 'billed_usd', 'marginUsd', 'margin_usd']

/** El mapa de métricas sin la de gasto: al cliente no se le entrega. */
function withoutModelSpend(metrics) {
  return Object.fromEntries(
    Object.entries(metrics).filter(([name]) => name !== MODEL_SPEND_METRIC),
  )
}

/**
 * La fila sin sus importes.
 *
 * Se quitan las claves en vez de ponerlas a `null`: un `costUsd: null` sigue
 * siendo un sitio donde escribir `formatMoney(row.costUsd)` y que compile.
 */
function withoutMoneyFields(row) {
  const source = object(row)
  return Object.fromEntries(Object.entries(source).filter(([key]) => !MONEY_FIELDS.includes(key)))
}

function list(value) {
  return Array.isArray(value) ? value : []
}

function object(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function text(value) {
  return typeof value === 'string' ? value : ''
}

/** `null` cuando no hay dato, para poder distinguirlo de un cero real. */
function number(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
